import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AppSettings } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useCalendar, useCalendarMutations, useProjects, useSettings, useUpdateSettings } from '@/hooks/queries';
import type { CalendarExceptionKind } from '@chronos/shared';
import { ConfirmButton } from '@/components/ui';
import { Card, ErrorNote, PageHeader, Spinner, SubmitButton } from '@/components/ui';

export function SettingsPage() {
  const { data, isLoading, error } = useSettings();
  const update = useUpdateSettings();
  const projects = useProjects({ status: 'ACTIVE', pageSize: 200 });
  const [form, setForm] = useState<AppSettings | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const purge = useMutation({
    mutationFn: () => apiFetch<{ deleted: number }>('/api/settings/purge-screenshots', { method: 'POST' }),
  });

  if (isLoading || !form) return <div className="flex justify-center py-16"><Spinner /></div>;

  const set = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => setForm({ ...form, [key]: value });

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Settings" description="Platform-wide monitoring and time-tracking policy." />
      <ErrorNote error={error ?? update.error ?? purge.error} />

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSaved(false);
          update.mutate(form, { onSuccess: () => setSaved(true) });
        }}
      >
        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Screenshot capture</h2>
            <p className="mt-0.5 text-xs text-slate-500">Applied by desktop clients the next time they sign in or refresh.</p>
          </div>

          <Field label="Capture interval" hint="Minutes between automatic captures while a timer is running.">
            <div className="flex items-center gap-2">
              <input type="number" min={1} max={120} className="input max-w-[7rem]" value={form.screenshotIntervalMinutes} onChange={(e) => set('screenshotIntervalMinutes', Number(e.target.value))} />
              <span className="text-sm text-slate-500">minutes</span>
            </div>
          </Field>

          <Field label="Retention" hint="Screenshots older than this are deleted automatically. Set 0 to keep them forever.">
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={3650} className="input max-w-[7rem]" value={form.screenshotRetentionDays} onChange={(e) => set('screenshotRetentionDays', Number(e.target.value))} />
              <span className="text-sm text-slate-500">days</span>
            </div>
          </Field>

          <Field label="Monitoring notice version" hint="Bump this to require everyone to re-accept the desktop monitoring notice.">
            <input className="input max-w-[7rem]" value={form.monitoringNoticeVersion} onChange={(e) => set('monitoringNoticeVersion', e.target.value)} />
          </Field>
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Idle detection</h2>
          </div>

          <Field label="Idle threshold" hint="No keyboard or mouse activity for this long counts as idle.">
            <div className="flex items-center gap-2">
              <input type="number" min={1} max={240} className="input max-w-[7rem]" value={form.idleTimeoutMinutes} onChange={(e) => set('idleTimeoutMinutes', Number(e.target.value))} />
              <span className="text-sm text-slate-500">minutes</span>
            </div>
          </Field>

          <Toggle
            label="Stop the timer when idle"
            hint="When off, idle time is only flagged in the desktop app rather than clocking the user out."
            checked={form.stopTimerOnIdle}
            onChange={(v) => set('stopTimerOnIdle', v)}
          />
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Leave applications</h2>
            <p className="mt-0.5 text-xs text-slate-500">Casual leave always needs an admin. These control how early it must be filed, and whether medical leave skips the queue.</p>
          </div>
          <Field label="Casual leave notice" hint="Working days of notice; weekends and holidays don't count. 0 = by the previous working day (Friday for a Monday); 1 = Thursday for a Monday; 2 = Wednesday.">
            <div className="flex items-center gap-2">
              <input type="number" min={0} max={30} step={1} className="input max-w-[6rem]" value={form.casualLeaveNoticeWorkingDays} onChange={(e) => set('casualLeaveNoticeWorkingDays', Number(e.target.value))} />
              <span className="text-sm text-slate-500">working days</span>
            </div>
          </Field>
          <Toggle
            label="Auto-approve medical leave"
            hint="On: medical leave is approved the moment it is filed. Off: it waits in Leave requests like casual leave, but can still be filed the same day."
            checked={form.autoApproveMedicalLeave}
            onChange={(v) => set('autoApproveMedicalLeave', v)}
          />
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Breaks</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Pick a project to log breaks against and a coffee button appears next to the timer. Anyone can log time to it,
              and the desktop app pauses screenshots while it runs. Leave it unset to hide the button.
            </p>
          </div>
          <Field label="Break project" hint="Create a project called e.g. “Break” first, then choose it here.">
            <select className="input max-w-[16rem]" value={form.breakProjectId ?? ''} onChange={(e) => set('breakProjectId', e.target.value || null)}>
              <option value="">None — no break button</option>
              {projects.data?.items.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Attendance</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              How tracked hours become present / half day / leave, and what counts as late. Nothing is stored — change a value and
              every month re-evaluates.
            </p>
          </div>

          <Field label="Timezone" hint="Days start and end in this zone. IANA name, e.g. Asia/Kolkata, Europe/London.">
            <input className="input max-w-[14rem]" value={form.attendanceTimezone} onChange={(e) => set('attendanceTimezone', e.target.value)} list="tz-list" />
            <datalist id="tz-list">
              {['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'UTC'].map((z) => <option key={z} value={z} />)}
            </datalist>
          </Field>

          <Field
            label="Tracking starts on"
            hint="The day this organisation began using TickTock. Working days before it are ignored — no absence, no deficit, no deduction — because there is no data for them, not because nobody worked. Leave still accrues from the start of the leave year; correct an opening balance with a grant. Leave blank to count every day."
          >
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="input max-w-[11rem]"
                value={form.attendanceStartsOn ?? ''}
                onChange={(e) => set('attendanceStartsOn', e.target.value || null)}
              />
              {form.attendanceStartsOn && (
                <button type="button" className="btn-ghost py-1 text-xs" onClick={() => set('attendanceStartsOn', null)}>
                  Clear
                </button>
              )}
            </div>
          </Field>

          <Field label="Weekly days off" hint="Fixed weekend. Individual weekends can be made working days in the calendar below.">
            <div className="flex gap-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => {
                const on = form.weekendDays.includes(i);
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => set('weekendDays', on ? form.weekendDays.filter((x) => x !== i) : [...form.weekendDays, i].sort())}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${on ? 'bg-slate-700 text-white' : 'border border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Workday" hint="Expected tracked work per day (breaks excluded) plus the break allowance shown alongside.">
            <div className="flex items-center gap-2">
              <input type="number" min={0.5} max={24} step={0.5} className="input max-w-[5rem]" value={form.workdayHours} onChange={(e) => set('workdayHours', Number(e.target.value))} />
              <span className="text-sm text-slate-500">h work +</span>
              <input type="number" min={0} max={8} step={0.25} className="input max-w-[5rem]" value={form.breakHours} onChange={(e) => set('breakHours', Number(e.target.value))} />
              <span className="text-sm text-slate-500">h break</span>
            </div>
          </Field>

          <Field
            label="Half day range"
            hint="Below the minimum the day is a leave. Between the two it is a half day. Above the maximum it is a full day, and the gap to the workday hours becomes a deficit instead of costing half a day."
          >
            <div className="flex items-center gap-2">
              <input
                type="number" min={0} max={24} step={0.5} className="input max-w-[5rem]"
                value={form.halfDayHours}
                onChange={(e) => set('halfDayHours', Number(e.target.value))}
              />
              <span className="text-sm text-slate-500">to</span>
              <input
                type="number" min={0} max={24} step={0.5} className="input max-w-[5rem]"
                value={form.halfDayMaxHours}
                onChange={(e) => set('halfDayMaxHours', Number(e.target.value))}
              />
              <span className="text-sm text-slate-500">hours</span>
            </div>
          </Field>

          <Field label="Late after" hint="First clock-in later than start + grace is marked late.">
            <div className="flex items-center gap-2">
              <input type="time" className="input max-w-[8rem]" value={form.workdayStart} onChange={(e) => set('workdayStart', e.target.value)} />
              <span className="text-sm text-slate-500">+</span>
              <input type="number" min={0} max={240} className="input max-w-[5rem]" value={form.lateGraceMinutes} onChange={(e) => set('lateGraceMinutes', Number(e.target.value))} />
              <span className="text-sm text-slate-500">min grace</span>
            </div>
          </Field>
        </Card>

        <Card className="divide-y divide-slate-100">
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold text-slate-800">Time entry</h2>
          </div>
          <Toggle
            label="Allow manual time entry"
            hint="When off, employees can only track time with the timer. Admins can still correct timesheets."
            checked={form.allowManualTimeEntry}
            onChange={(v) => set('allowManualTimeEntry', v)}
          />
          <Field label="Daily timer cut-off" hint="For people who forget to clock out. Every running timer is stopped at this time, in the attendance timezone, and the entry ends exactly then. No timer can be started again until midnight. Empty = off.">
            <div className="flex items-center gap-2">
              <input type="time" className="input max-w-[8rem]" value={form.timerCutoffTime ?? ''} onChange={(e) => set('timerCutoffTime', e.target.value || null)} />
              {form.timerCutoffTime && (
                <button type="button" className="btn-ghost py-1 text-xs" onClick={() => set('timerCutoffTime', null)}>
                  Turn off
                </button>
              )}
            </div>
          </Field>
          <Toggle
            label="Notify on screenshot capture"
            hint="The desktop app shows a small notification each time it captures. Turning this off silences it for everyone; people can already silence their own machine from the tray. The first-run monitoring notice is separate and always shown."
            checked={form.notifyOnCapture}
            onChange={(v) => set('notifyOnCapture', v)}
          />
        </Card>

        <div className="flex items-center gap-3">
          <SubmitButton pending={update.isPending} className="btn-primary">Save settings</SubmitButton>
          {saved && !update.isPending && <span className="text-sm text-emerald-700">Saved.</span>}
        </div>
      </form>

      <CalendarEditor />

      <Card className="mt-6 p-5">
        <h2 className="text-sm font-semibold text-slate-800">Run the retention purge now</h2>
        <p className="mt-1 text-xs text-slate-500">
          Deletes every screenshot older than the retention window immediately, instead of waiting for the nightly job.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button type="button" className="btn-secondary" onClick={() => purge.mutate()} disabled={purge.isPending || form.screenshotRetentionDays === 0}>
            {purge.isPending && <Spinner className="h-4 w-4" />} Purge expired screenshots
          </button>
          {purge.data && <span className="text-sm text-slate-600">{purge.data.deleted} screenshot(s) deleted.</span>}
        </div>
      </Card>
    </div>
  );
}

/** Holidays and working weekends. Saves on add/remove; no Save button needed. */
function CalendarEditor() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const { data, isLoading } = useCalendar(year);
  const { upsert, remove } = useCalendarMutations();
  const [date, setDate] = useState('');
  const [kind, setKind] = useState<CalendarExceptionKind>('HOLIDAY');
  const [name, setName] = useState('');

  const weekdayOf = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
  const prettyDate = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Holidays and working weekends</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            A <b>holiday</b> makes a weekday off. A <b>working day</b> makes a Saturday or Sunday count. Changes apply immediately.
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button className="btn-secondary py-1" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">←</button>
          <span className="min-w-[4rem] text-center text-sm font-medium">{year}</span>
          <button className="btn-secondary py-1" onClick={() => setYear((y) => y + 1)} aria-label="Next year">→</button>
        </div>
      </div>

      <form
        className="flex flex-wrap items-end gap-2 border-b border-slate-100 bg-slate-50 px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!date || !name.trim()) return;
          upsert.mutate({ date, kind, name: name.trim() }, { onSuccess: () => { setName(''); setDate(''); } });
        }}
      >
        <div>
          <label className="label" htmlFor="cal-date">Date</label>
          <input id="cal-date" type="date" className="input" required value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="cal-kind">Type</label>
          <select id="cal-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as CalendarExceptionKind)}>
            <option value="HOLIDAY">Holiday (day off)</option>
            <option value="WORKING">Working day (weekend on)</option>
          </select>
        </div>
        <div className="min-w-[12rem] flex-1">
          <label className="label" htmlFor="cal-name">Name</label>
          <input id="cal-name" className="input" required maxLength={120} placeholder={kind === 'HOLIDAY' ? 'e.g. Diwali' : 'e.g. Release weekend'} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <SubmitButton pending={upsert.isPending} className="btn-primary">Add</SubmitButton>
      </form>
      <ErrorNote error={upsert.error ?? remove.error} />

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-slate-500">No holidays or working weekends set for {year}. Weekends are off, every other day is a workday.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {data?.map((x) => (
            <li key={x.id} className="flex items-center gap-3 px-5 py-2.5">
              <span className={`badge ${x.kind === 'HOLIDAY' ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                {x.kind === 'HOLIDAY' ? 'Holiday' : 'Working'}
              </span>
              <span className="w-24 font-mono text-xs tabular-nums text-slate-500">{weekdayOf(x.date)} {prettyDate(x.date)}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{x.name}</span>
              <ConfirmButton className="btn-ghost px-2 py-1 text-xs text-rose-600" label="Remove" confirmLabel="Confirm" onConfirm={() => remove.mutate(x.id)} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <Field label={label} hint={hint}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition ${checked ? 'bg-brand-600' : 'bg-slate-300'}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[1.375rem]' : 'left-0.5'}`} />
      </button>
    </Field>
  );
}
